import { useId, useRef, useState, type ReactNode } from "react";
import { money, propPayoutTax, type Dec } from "@ftr/tax-core";
import { Figure } from "./figure.tsx";
import { MoneyField, WhyPanel } from "./ui.tsx";
import { Link } from "../lib/router.tsx";
import { parseMoney } from "../lib/fields.ts";
import { pct, usd } from "../lib/format.ts";
import { useActiveStep } from "../lib/scroll.ts";

// The visitor picks the payouts (and can change the costs); the base is the hero demo's: single filer, no other income.
const PRESETS = ["10,000", "30,000", "75,000", "150,000"];
const START_PAYOUTS = "30,000";
const START_COSTS = "1,800";

function run(gross: Dec, expenses: Dec) {
  const r = propPayoutTax(
    {
      status: "single",
      wages: money("0"),
      traderSocialSecurityWages: money("0"),
      medicareWages: money("0"),
      withholding: money("0"),
      interest: money("0"),
      otherOrdinaryIncome: money("0"),
      otherPropNetProfit: money("0"),
      net1256: money("0"),
      qbi: { include: true, specifiedService: true },
    },
    gross,
    expenses,
  );
  return r.state === "computed" ? r : null;
}

type Result = NonNullable<ReturnType<typeof run>>;
interface Example {
  gross: Dec;
  expenses: Dec;
  r: Result;
}

/** Reads the two fields. A blank cost counts as $0 and says so; anything that isn't a usable amount is a message. */
function readInputs(payouts: string, costs: string): { ex: Example } | { message: string } {
  const p = parseMoney(payouts);
  const c = parseMoney(costs);
  if (p.kind === "empty") return { message: "Pick an amount or type your payouts to start." };
  if (p.kind === "error" || p.value.lessThan(0)) return { message: "Enter your payouts as an amount like 30,000." };
  if (c.kind === "error" || (c.kind === "value" && c.value.lessThan(0))) return { message: "Enter your costs as an amount like 1,800, or leave it blank." };
  const gross = p.value;
  const expenses = c.kind === "value" ? c.value : money("0");
  if (!gross.minus(expenses).greaterThan(0)) return { message: "With costs at or above your payouts there's no profit, so there's no tax on it here." };
  const r = run(gross, expenses);
  return r && r.setAsideRate ? { ex: { gross, expenses, r } } : { message: "We couldn't work out this example. Try the full calculator." };
}

function F({ v }: { v: Dec }) {
  return <strong className="figure">{usd(v)}</strong>;
}

interface Step {
  title: ReactNode;
  body: ReactNode;
  /** What the card's headline shows while this step is the one being read. */
  focus: { label: string; value: Dec; kind: "usd" | "pct"; sub?: string };
}

/**
 * Scroll-told walk of the visitor's own prop year through the tax math. They pick the payouts first, then each
 * step explains one slice. The steps are plain text in reading order; the card beside them is a picture of the same
 * figures, so it is hidden from assistive tech.
 */
export function PayoutStory({ calculatorHref }: { calculatorHref: string }) {
  const [payouts, setPayouts] = useState(START_PAYOUTS);
  const [costs, setCosts] = useState(START_COSTS);
  const read = readInputs(payouts, costs);
  // While a field is mid-edit or unusable, the walkthrough keeps showing the last good numbers under the message.
  const last = useRef<Example | null>(null);
  if ("ex" in read) last.current = read.ex;
  const ex = "ex" in read ? read.ex : last.current;
  const presetsId = useId();
  const costsBlank = parseMoney(costs).kind === "empty";

  return (
    <section className="story" aria-labelledby="story-title">
      <div className="shell">
        <div className="story-head">
          <h2 id="story-title">How your prop payouts get taxed</h2>
          <p className="band-lede">Pick an amount or type your own, then scroll. Each step shows where the money goes, using the same math as the calculators.</p>
          <div className="story-controls">
            <div className="story-presets">
              <span className="story-presets-label" id={presetsId}>
                Payouts this year
              </span>
              <div className="choice-row" role="group" aria-labelledby={presetsId}>
                {PRESETS.map((v) => (
                  <button key={v} type="button" className={"chip" + (payouts === v ? " chip--on" : "")} aria-pressed={payouts === v} onClick={() => setPayouts(v)}>
                    ${v}
                  </button>
                ))}
              </div>
            </div>
            <MoneyField label="Or type your own" value={payouts} onChange={setPayouts} />
            <MoneyField label="Your costs" value={costs} onChange={setCosts} optional hint="Evaluations, resets and data" />
          </div>
          {"message" in read && (
            <p className="story-message" role="status">
              {read.message}
            </p>
          )}
          <p className="story-note">
            For one person filing alone, with no other job, under 2026 federal rules.{costsBlank ? " No costs entered, so none are taken off." : ""} Your
            real numbers will be different.
          </p>
        </div>
        {ex && <Walkthrough ex={ex} />}
        <div className="story-end">
          <p>
            A job, a spouse or other income will change these numbers. <Link href={calculatorHref}>Run the full calculator on your year →</Link>
          </p>
          {ex && <WhyPanel trace={ex.r.withPayouts.trace} title="Why these amounts?" />}
        </div>
      </div>
    </section>
  );
}

function Walkthrough({ ex }: { ex: Example }) {
  const { gross, expenses, r } = ex;
  const rate = r.setAsideRate!;
  const perThousand = rate.times(1000);
  const qbi = r.withPayouts.qbiDeduction;

  const steps: Step[] = [
    {
      title: "You get paid",
      body: (
        <>
          Your payouts add up to <F v={gross} /> this year. The prop firm doesn't take any tax out, so all of it lands in your account. In January it
          sends you a 1099 showing the total.
        </>
      ),
      focus: { label: "Payouts for the year", value: gross, kind: "usd", sub: "No tax taken out" },
    },
    {
      title: "Take off your costs",
      body: expenses.isZero() ? (
        <>With no costs entered, your profit for the year is the full <F v={r.netProfit} />.</>
      ) : (
        <>
          Evaluations, resets and data might cost you around <F v={expenses} />. Those come off first, so your profit for the year would be <F v={r.netProfit} />.
        </>
      ),
      focus: { label: "Profit after costs", value: r.netProfit, kind: "usd" },
    },
    {
      title: "Self-employment tax",
      body: (
        <>
          You work for yourself, so you pay Social Security and Medicare tax on most of that profit. That comes to <F v={r.seTaxPart} />. An employer
          would normally pay half of it, but here it's all on you.
        </>
      ),
      focus: { label: "Self-employment tax", value: r.seTaxPart, kind: "usd" },
    },
    {
      title: "Income tax",
      body: (
        <>
          {qbi.greaterThan(0) ? (
            <>The standard deduction, half of your self-employment tax and the qualified business income deduction of {usd(qbi)}</>
          ) : (
            <>The standard deduction and half of your self-employment tax</>
          )}{" "}
          come off first, so{" "}
          {usd(r.withPayouts.taxableIncome)} of your {usd(r.netProfit)} is taxed. The income tax on that is <F v={r.incomeTaxPart} />.
        </>
      ),
      focus: { label: "Income tax", value: r.incomeTaxPart, kind: "usd" },
    },
    {
      title: <>Put away {pct(rate)} of your profit</>,
      body: (
        <>
          Your federal tax for the year is <F v={r.taxOnPayouts} />, about {usd(perThousand)} for every $1,000 of profit. Move that to a savings account
          as payouts come in and you keep <F v={r.takeHome} />. Your state may add more.
        </>
      ),
      focus: { label: "Put away from profit", value: rate, kind: "pct", sub: `${usd(r.taxOnPayouts)} federal tax · you keep ${usd(r.takeHome)}` },
    },
  ];

  const [active, listRef] = useActiveStep<HTMLOListElement>(steps.length);
  const focus = steps[active]!.focus;
  const share = (v: Dec) => v.div(gross).times(100).toFixed(2) + "%";
  // The bar is the year's payouts. Each step carves its slice out of what is still yours.
  const slices = [
    { tone: "quiet", value: expenses, at: 1 },
    { tone: "warn", value: r.seTaxPart, at: 2 },
    { tone: "ink", value: r.incomeTaxPart, at: 3 },
  ];
  const ledger = [
    { label: "Payouts", value: usd(gross), tone: "good", at: 0 },
    { label: "Costs", value: usd(expenses.neg()), tone: "quiet", at: 1 },
    { label: "Self-employment tax", value: usd(r.seTaxPart.neg()), tone: "warn", at: 2 },
    { label: "Income tax", value: usd(r.incomeTaxPart.neg()), tone: "ink", at: 3 },
    { label: "You keep", value: usd(r.takeHome), tone: "good", at: 4, strong: true },
  ];

  return (
    <div className="story-grid">
      <ol className="story-steps" ref={listRef}>
        {steps.map((s, i) => (
          <li key={i} className={"story-step" + (i < active ? " story-step--done" : i === active ? " story-step--on" : "")}>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
          </li>
        ))}
      </ol>
      <div className="story-card" aria-hidden="true">
        <div className="demo-top">
          <span className="demo-label">Your payouts, 2026</span>
          <span className="demo-amount">{usd(gross)}</span>
        </div>
        <div className="story-bar">
          {slices.map((s) => (
            <span key={s.tone} className={"story-seg seg--" + s.tone} style={{ width: active >= s.at ? share(s.value) : "0%" }} />
          ))}
          <span className="story-seg story-seg--rest seg--good" />
        </div>
        <div className="story-focus">
          <span className="demo-label">{focus.label}</span>
          <span className="story-big">
            <Figure key={focus.kind} value={focus.value} kind={focus.kind} places={1} />
          </span>
          <span className="story-sub">{focus.sub ?? ""}</span>
        </div>
        <ul className="story-ledger">
          {ledger.map((l) => (
            <li key={l.label} className={(l.at > active ? "is-todo" : "") + (l.strong ? " is-strong" : "")}>
              <span>
                <span className={"dot seg--" + l.tone} />
                {l.label}
              </span>
              <span className="figure">{l.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
